import * as api from '../../api';
import { Stack, ActionButton, Panel, PanelType, Link, Modal } from "@fluentui/react";
import { FunctionComponent, useMemo, useState } from "react";
import { CargoGrid, CargoRemaining } from "../../components";
import { HaulSize } from "../../components/BigSiteTable/BigSiteTable";
import { getSiteType, primaryPortsT1, primaryPortsT2, primaryPortsT3 } from "../../site-data";
import { appTheme, cn } from "../../theme";
import { mergeCargo, isMobile, getCargoCountOnHand, sumCargo } from "../../util";
import { getAvgHaulCosts } from "../../avg-haul-costs";
import { store } from "../../local-storage";
import { KnownFC } from '../../types';
import { ModalCommander } from '../../components/ModalCommander';
import { CalloutMsg } from '../../components/CalloutMsg';

export const HaulList: FunctionComponent<{ buildTypes: string[], size?: number, isPrimary?: boolean, shopNearSystem?: string }> = (props) => {
  const [showList, setShowList] = useState(false);
  const [knownFC, setKnownFC] = useState<KnownFC[]>([]);
  const [showCmdr, setShowCmdr] = useState(false);
  const [refetch, setRefetch] = useState(0);

  let totalHaul = 0;
  const cargos = [];
  const mapBuildTypeCount: Record<string, number> = {};
  for (const buildType of props.buildTypes) {
    const haulCost = getAvgHaulCosts(buildType);
    const total = Object.values(haulCost).reduce((sum, val) => sum += val, 0);
    totalHaul += total;
    if (haulCost) {
      cargos.push(haulCost);
      mapBuildTypeCount[buildType] = (mapBuildTypeCount[buildType] ?? 0) + 1;
    }
  }

  const cargoNeeded = mergeCargo(cargos);
  const cargoRemaining = sumCargo(cargoNeeded);
  const fcCargo = mergeCargo(knownFC.map(fc => fc.cargo));
  const cargoOnHand = getCargoCountOnHand(cargoNeeded, fcCargo)
  const fcRemaining = cargoRemaining - cargoOnHand;

  const rows = Object.entries(mapBuildTypeCount).map(([buildType, count]) => {
    const type = getSiteType(buildType)!;
    return <div key={`asl-${buildType}`}>
      {count} x {type.displayName2} ({buildType})
    </div>;
  });

  useMemo(async () => {
    if (!store.cmdrName || !showList) { return []; }

    try {
      console.debug(`Fetch FCs #${refetch + 1}`);
      const cmdrFCs = await api.cmdr.getCmdrLinkedFCs(store.cmdrName);
      setKnownFC(cmdrFCs);
    } catch (err: any) {
      if (err.statusCode !== 404) {
        // ignore cases where a project is not found
        console.error(`HaulList-getCmdrLinkedFCs: ${err.stack}`);
      }
    }
    return [];
  }, [refetch, showList]);

  const width = 480 + knownFC.length * 80;
  let msg = undefined;
  if (props.buildTypes.length === 1) {

    if (primaryPortsT1.includes(props.buildTypes[0])) {
      msg = "Tier 1 primary ports will be more expensive by ~16%";
    }
    else if (primaryPortsT2.includes(props.buildTypes[0])) {
      msg = "Tier 2 primary ports will be more expensive by ~33%";
    }
    else if (primaryPortsT3.includes(props.buildTypes[0])) {
      msg = "Tier 3 primary ports will be more expensive by ~20%";
    }
  }

  return <>
    <Stack horizontal verticalAlign='center' style={{ width: 300 }}>
      <HaulSize haul={totalHaul} size={props.size} />

      <ActionButton
        className={cn.bBox}
        iconProps={{ iconName: 'DeliveryTruck' }}
        text={`~${totalHaul.toLocaleString()} units`}
        style={{ height: 24 }}
        onClick={() => setShowList(true)}
      />

      {props.isPrimary === true && <span style={{ marginLeft: 6, fontSize: 12, color: appTheme.palette.accent }}>(primary port)</span>}
      {props.isPrimary === false && <span style={{ marginLeft: 6, fontSize: 12, color: appTheme.palette.accent }}>(non-primary)</span>}
      {props.isPrimary === undefined && msg && <CalloutMsg msg={msg} iconStyle={{ marginTop: 6 }} />}
    </Stack>

    {showList && <>
      <Panel
        isOpen
        isLightDismiss
        headerText='Approximate shopping list:'
        allowTouchBodyScroll={isMobile()}
        type={PanelType.custom}
        customWidth={`${width}px`}
        styles={{
          header: { textTransform: 'capitalize', cursor: 'default' },
          overlay: { backgroundColor: appTheme.palette.blackTranslucent40, cursor: 'default' },
        }}
        onDismiss={(ev: any) => {
          setShowList(false);
        }}

        onRenderFooterContent={() => {
          return <>
            <CargoRemaining sumTotal={cargoRemaining} label='Remaining cargo' />
            <CargoRemaining sumTotal={fcRemaining} label='Fleet Carrier deficit' />
          </>;
        }}
      >

        <div style={{
          color: appTheme.palette.themeSecondary,
          margin: 4,
          fontSize: 12,
        }}>
          {rows}
        </div>

        <div style={{ textAlign: 'right' }}>
          <Link
            style={{ fontSize: 10 }}
            onClick={() => setShowCmdr(true)}
          >
            Track a Commander Linked Fleet Carrier?
          </Link>

          {showCmdr && <Modal
            isOpen
            styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}
          >
            <ModalCommander onComplete={() => setShowCmdr(false)} preAddFC />
          </Modal>}
        </div>

        <CargoGrid
          cargo={cargoNeeded}
          linkedFC={knownFC}
          onRefresh={() => setRefetch(refetch + 1)}
          whereToBuy={props.shopNearSystem ? { refSystem: props.shopNearSystem, buildIds: [] } : undefined}
        />
      </Panel>
    </>}
  </>;
};
